import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Star, Heart, Send, PartyPopper } from "lucide-react";
import { openRate, shouldPromptRating, snoozeRating, markRateDone, hasRated, PLAY_STORE_PUBLISHED } from "@/lib/rate";
import { isAndroidApp } from "@/lib/twa";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
} from "@/components/ui/alert-dialog";

// In-app rating system (TWA/PWA safe — no native popups).
// Auto-prompts engaged users; also openable from the "Rate Air Wave" menu item.
// Ratings are saved to the DB; happy users get a Play Store nudge once we're live,
// unhappy users are routed to private feedback so it never hits the store.
export const RatePrompt = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [review, setReview] = useState("");
  const [step, setStep] = useState("rate"); // rate | review | thanks
  const [busy, setBusy] = useState(false);

  const start = useCallback(() => {
    setStars(0); setHover(0); setReview(""); setStep("rate"); setOpen(true);
  }, []);

  // Auto-prompt engaged, signed-in users.
  useEffect(() => {
    if (!user) return;
    const t = setTimeout(() => { if (shouldPromptRating()) start(); }, 6000);
    return () => clearTimeout(t);
  }, [user, start]);

  // Manual open from the menu ("Rate Air Wave").
  useEffect(() => {
    const h = () => start();
    window.addEventListener("open-rating", h);
    return () => window.removeEventListener("open-rating", h);
  }, [start]);

  const close = () => { snoozeRating(); setOpen(false); };

  const pickStar = (n) => {
    setStars(n);
    setStep("review");
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/ratings", { stars, review: review.trim(), is_app: isAndroidApp() });
      markRateDone();
      if (stars >= 4 && PLAY_STORE_PUBLISHED) {
        openRate();
        setOpen(false);
        return;
      }
      setStep("thanks");
    } catch (e) {
      toast.error("Couldn't save your rating. Please try again.");
    } finally { setBusy(false); }
  };

  const positive = stars >= 4;

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <AlertDialogContent data-testid="rate-prompt" className="border-white/10 bg-[#0C0C14] text-white">
        {step === "rate" && (
          <>
            <div className="mx-auto -mb-1 grid h-14 w-14 place-items-center rounded-2xl bg-[#FFD600]/15">
              <Heart size={26} className="text-[#FFD600]" />
            </div>
            <AlertDialogHeader className="text-center sm:text-center">
              <AlertDialogTitle className="text-center text-xl">Enjoying Air Wave?</AlertDialogTitle>
              <AlertDialogDescription className="text-center text-white/70">
                Thanks for riding the wave with us! Tap the stars to rate your experience. 💛
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="my-2 flex items-center justify-center gap-2" data-testid="rate-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  data-testid={`rate-star-${n}`}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  onClick={() => pickStar(n)}
                  aria-label={`${n} star${n > 1 ? "s" : ""}`}
                  className="transition-transform hover:scale-125 active:scale-110">
                  <Star
                    size={36}
                    className={(hover || stars) >= n ? "fill-[#FFD600] text-[#FFD600]" : "text-white/25"}
                  />
                </button>
              ))}
            </div>
            <button onClick={close} data-testid="rate-later-btn"
              className="w-full py-1 text-xs text-white/40 transition-colors hover:text-white/70">
              Maybe later
            </button>
          </>
        )}

        {step === "review" && (
          <>
            <div className="mx-auto -mb-1 flex items-center justify-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} size={22} className={stars >= n ? "fill-[#FFD600] text-[#FFD600]" : "text-white/20"} />
              ))}
            </div>
            <AlertDialogHeader className="text-center sm:text-center">
              <AlertDialogTitle className="text-center text-xl">
                {positive ? "Awesome — thank you! 🎉" : "Thanks — we want to do better"}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-center text-white/70">
                {positive
                  ? "Anything you'd like to add? (optional)"
                  : "Sorry it wasn't perfect. Tell us what we can improve — this goes straight to our team, privately."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <textarea
              data-testid="rate-review-input"
              value={review}
              onChange={(e) => setReview(e.target.value)}
              placeholder={positive ? "What do you love about Air Wave?" : "What went wrong?"}
              className="min-h-[96px] w-full resize-y rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none transition-colors focus:border-[#00FFC2]"
            />
            <button onClick={submit} disabled={busy} data-testid="rate-submit-btn"
              className="mt-1 flex w-full items-center justify-center gap-2 rounded-full bg-[#00FFC2] py-3 font-bold text-black transition-transform hover:-translate-y-0.5 disabled:opacity-60">
              <Send size={16} /> {busy ? "Sending…" : positive && PLAY_STORE_PUBLISHED ? "Submit & rate on Play Store" : "Submit"}
            </button>
          </>
        )}

        {step === "thanks" && (
          <>
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-[#00FFC2]/15">
              <PartyPopper size={26} className="text-[#00FFC2]" />
            </div>
            <AlertDialogHeader className="text-center sm:text-center">
              <AlertDialogTitle className="text-center text-xl">Thank you! 🙌</AlertDialogTitle>
              <AlertDialogDescription className="text-center text-white/70">
                {positive
                  ? "Your feedback means the world to us. We'll invite you to rate us on the Play Store as soon as we launch there!"
                  : "We've received your feedback and our team will use it to make Air Wave better."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <button onClick={() => setOpen(false)} data-testid="rate-done-btn"
              className="mt-1 w-full rounded-full bg-[#00FFC2] py-3 font-bold text-black transition-transform hover:-translate-y-0.5">
              Done
            </button>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};
